import Game from './Game';
import GameObject from './GameObject/GameObject';
import { GameActions } from './State/Game/GameActions';
import { PlayerActions } from './State/Player/PlayerActions';
import { ILevelDataMap } from './State/Player/IPlayerState';
import { getNewLevelData } from './State/Player/PlayerLevelHelper';
import CardsHelper from './Utility/CardsHelper';
import { LevelType } from './State/Level/ILevelState';

let cheatsEnabled = false;

declare global {
    interface Window {
        game: typeof Game;
        GameObject: typeof GameObject;
        getCardsHelper: () => CardsHelper;
        key: { [key: string]: boolean };
        cheats: any;
    }
}

export function isCheatsEnabled() {
    return cheatsEnabled;
}

export function enableCheats() {
    if (cheatsEnabled) return;
    cheatsEnabled = true;

    // Track key presses for "shift-key" auto-match cheat.
    window.key = {};
    window.addEventListener('keydown', e => {
        window.key[e.key] = true;
    });
    window.addEventListener('keyup', e => {
        window.key[e.key] = false;
    });

    // Expose paths in global scope.
    window.game = Game;
    window.GameObject = GameObject;
    window.getCardsHelper = () => CardsHelper.instance;

    window.cheats = {
        loadLevel: (level: number, path: string, type?: LevelType) => {
            Game.state.dispatch(GameActions.loadLevel(level, path, type));
        },
        dumpPlayerData: () => {
            Game.sdk.restore().then(data => {
                console.log(data);
            });
        },
        clearPlayerData: () => {
            // tslint:disable-next-line:no-console
            console.log('Clearing player data - please wait...');
            Game.sdk.clearPlayerData().then(() => {
                // tslint:disable-next-line:no-console
                console.log('Player data cleared. Please restart the game.');
            });
        },
        resetFriendData: () => {
            Game.state.dispatch(
                PlayerActions.setPlayerState({
                    ...Game.state.getState().playerState,
                    friendData: [],
                })
            );
        },
        resetTimers: () => {
            Game.state.dispatch(
                PlayerActions.setPlayerState({
                    ...Game.state.getState().playerState,
                    lifeTimer: null,
                    gateTimer: null,
                    missionTimer: null,
                    keyMissionTimer: null,
                    lifeSharePrizeTimer: null,
                    wheelTimer: null,
                    friendData: Game.state
                        .getState()
                        .playerState.friendData.map(friend => ({
                            ...friend,
                            lifeSendTimer: null,
                            lifeRequestTimer: null,
                            giftTimer: null,
                        })),
                })
            );
        },
        setCoins: (coins: number) => {
            Game.state.dispatch(
                PlayerActions.receiveCoins(
                    coins - Game.state.getState().playerState.coins
                )
            );
        },
        setLives: (lives: number) => {
            Game.state.dispatch(
                PlayerActions.receiveLives(
                    lives - Game.state.getState().playerState.lives
                )
            );
        },
        setKeys: (keys: number) => {
            Game.state.dispatch(
                PlayerActions.receiveKeys(
                    keys - Game.state.getState().playerState.keys
                )
            );
        },
        setLevel: (level: number, stars: number = 3) => {
            const levelData: ILevelDataMap = { saga: [], challenge: [] };
            for (let i = 1; i <= level; i++) {
                levelData.saga.push(getNewLevelData({ levelNumber: i, stars }));
            }

            Game.state.dispatch(
                PlayerActions.setPlayerState({
                    ...Game.state.getState().playerState,
                    levels: levelData,
                    world: Math.ceil(level / 60),
                })
            );
        },
        setWorld: (world: number) => {
            Game.state.dispatch(
                PlayerActions.setPlayerState({
                    ...Game.state.getState().playerState,
                    world: world,
                })
            );
        },
        setBoosterTNT: (amount: number) => {
            const playerState = Game.state.getState().playerState;
            Game.state.dispatch(
                PlayerActions.receiveBoosterTNT(
                    amount - playerState.inventory.boosterTNT
                )
            );
        },
        setBoosterBullsEye: (amount: number) => {
            const playerState = Game.state.getState().playerState;
            Game.state.dispatch(
                PlayerActions.receiveBoosterBullsEye(
                    amount - playerState.inventory.boosterBullsEye
                )
            );
        },
        setBoosterShuffle: (amount: number) => {
            const playerState = Game.state.getState().playerState;
            Game.state.dispatch(
                PlayerActions.receiveBoosterShuffle(
                    amount - playerState.inventory.boosterShuffle
                )
            );
        },
        setBoosterUndo: (amount: number) => {
            const playerState = Game.state.getState().playerState;
            Game.state.dispatch(
                PlayerActions.receiveBoosterUndo(
                    amount - playerState.inventory.boosterUndo
                )
            );
        },
        setBoosterJoker: (amount: number) => {
            const playerState = Game.state.getState().playerState;
            Game.state.dispatch(
                PlayerActions.receiveBoosterJoker(
                    amount - playerState.inventory.boosterJoker
                )
            );
        },
        setExtraCards: (cards: number) => {
            const playerState = Game.state.getState().playerState;
            Game.state.dispatch(
                PlayerActions.receiveExtraCards(
                    cards - playerState.inventory.extraCards
                )
            );
        },
        unlockBackcard: (backcardId?: number) => {
            Game.state.dispatch(PlayerActions.unlockBackCard(backcardId));
        },
        setMissionProgress: (mission: number, progress: number) => {
            const playerState = Game.state.getState().playerState;
            if (mission < 1 || mission > 4) {
                // tslint:disable-next-line:no-console
                /* prettier-ignore */ console.log('Please enter a valid mission number between 1 and 4.');
                return;
            }
            Game.state.dispatch(
                PlayerActions.setMissionProgress(
                    mission - 1,
                    progress - playerState.missions[mission - 1].progress
                )
            );
        },
        setWheelSpins: (spins: number) => {
            const playerState = Game.state.getState().playerState;
            Game.state.dispatch(
                PlayerActions.receiveWheelSpins(spins - playerState.wheelSpins)
            );
        },
    };
}

const konami: string = '38384040373937396665';
let input = '';
window.addEventListener('keyup', event => {
    if (!isCheatsEnabled()) {
        input += event.keyCode;
        if (input !== konami.substr(0, input.length)) {
            input = '';
        } else if (input === konami) {
            /* tslint:disable:max-line-length */ /* tslint:disable:no-console*/ /* prettier-ignore */ console.log('**********************************************************************');
            /* tslint:disable:max-line-length */ /* tslint:disable:no-console*/ /* prettier-ignore */ console.log('*************************** CHEATS ENABLED ***************************');
            /* tslint:disable:max-line-length */ /* tslint:disable:no-console*/ /* prettier-ignore */ console.log('**********************************************************************');
            enableCheats();
        }
    }
});
